/**
 * Public-sentiment evidence probe — the one predicate that answers
 * "does this site's sentiment scan actually carry evidence?".
 *
 * Extracted from `reportGeneration/sectionOmissions.ts` so the section-fallback
 * composers can consult it too. Those composers run BEFORE the omission stamp
 * and, until they could see this, filled all eight empty sections with
 * "pending re-run" placeholders — which made the omission probe unreachable,
 * because a populated section is never omitted.
 *
 * Kept deliberately dependency-light (types + `detectBaselineSentiment`) so it
 * can be imported from both the fallback registry and the evidence-text
 * builder without creating a cycle.
 */
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { detectBaselineSentiment } from './detectBaselineSentiment';

export interface PublicSentimentEvidenceProbe {
  /**
   * TRUE when the scan RAN and provably produced nothing usable: sources were
   * attempted, zero tier-1 facts came back, and there are either no entries, an
   * unavailable status, or only classifier-BASELINE entries (default values
   * with no meaningful variance).
   *
   * Regenerating prose alone cannot resolve this gap. Collection may have
   * failed, so source coverage must be disclosed and a later scan may help.
   */
  empty: boolean;
  /** Count of tier-1 + legacy sources the scan attempted. */
  attempted: number;
  /**
   * TRUE when no scan has been run at all — an absent section, or one that
   * attempted zero sources.
   *
   * Held separate from `empty` because the two license OPPOSITE sentences. A
   * scan that never ran genuinely can be fixed by running it, so "pending
   * re-run" is honest there. A scan that ran and found nothing cannot, and
   * the report must distinguish an unattempted scan from failed or empty
   * collection without claiming that unobserved evidence does not exist.
   */
  neverSearched: boolean;
}

export function probePublicSentimentEvidence(
  sed: SiteEvidenceDocument | null | undefined,
): PublicSentimentEvidenceProbe {
  const section = sed?.publicSentiment;
  if (!section || typeof section !== 'object') {
    return { empty: false, attempted: 0, neverSearched: true };
  }
  const attempted =
    (section.tier1SourcesAttempted ?? []).length + (section.sourcesAttempted ?? []).length;
  const facts = (section.tier1Facts ?? []).length
    + (sed?.documentFacts ?? []).filter(fact => fact.sedSections?.includes('publicSentiment') && !!fact.sourceQuote?.trim()).length;
  // Discovery leads are useful source context, never observed public opinion.
  // Keeping them selects the cited context report and its outreach contract.
  const sourceLeadContext = (section.sourceLeads ?? []).some(lead => /^https:\/\//i.test(lead.url)
    && !!lead.title?.trim() && !!lead.classification?.trim() && Array.isArray(lead.limitations) && lead.limitations.length > 0);
  const assessedLocalStance = section.assessment?.method === 'dated-local-explicit-stance-v1'
    && section.assessment.status === 'assessed' && section.assessment.localObservationCount > 0;
  const entries = (section.entries ?? []).length;
  // Live 2026-08-11 (Redhead): the scan yielded baseline-default observations,
  // so an `entries === 0` test alone never fired and the empty sections kept
  // blocking. `detectBaselineSentiment` is the same predicate the coverage gate
  // uses, so the two agree on what "nothing usable" means.
  //
  // It is handed a NORMALISED section. `entries` and `perTech` are required by
  // the type and that helper dereferences both without guarding, which was safe
  // while the only callers were the coverage gate and the generation-time
  // omission stamp. This probe now runs inside the section-fallback composers,
  // i.e. on every public-sentiment generation, so a legacy or partially-written
  // section missing either array would turn a cosmetic gap into a crash during
  // report generation. Defaulting here costs nothing and keeps that blast
  // radius closed without changing the helper's own contract.
  const noUsableEntries =
    entries === 0
    || section.status === 'unavailable'
    || detectBaselineSentiment({
      ...section,
      entries: section.entries ?? [],
      perTech: section.perTech ?? [],
    });
  return {
    empty: attempted > 0 && facts === 0 && !sourceLeadContext && noUsableEntries && !assessedLocalStance,
    attempted,
    neverSearched: attempted === 0,
  };
}
